/**
 * Faithless storefront enhancements.
 * Small, dependency-free custom elements on top of Horizon:
 *  - <fl-video>      lazy background video from Shopify Files (poster first)
 *  - <fl-countdown>  drop countdown (password page)
 *  - <fl-flip>       tap to turn a shirt (front / back)
 *  - <fl-quick-buy>  size chips + add to cart (opens Horizon's cart drawer)
 *  - [data-fl-add-variant] one-tap add buttons (cart upsell)
 *  - [data-fl-dialog-open] / <dialog> helpers (size guide)
 */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const saveData = Boolean(navigator.connection && navigator.connection.saveData);

/* ------------------------------------------------------------------ */
/* Cart helper                                                         */
/* ------------------------------------------------------------------ */

/**
 * Adds a variant to the cart and lets Horizon refresh/open the cart drawer:
 * the AJAX API (with the rendered cart sections) plus the same standard event
 * Horizon's own product form dispatches. Shopify's cart action is only a fallback.
 * @param {string|number} variantId
 * @param {number} quantity
 * @param {HTMLElement} source
 */
async function addToCart(variantId, quantity = 1, source = document.body) {
  let deferred = null;
  let CartLinesUpdateEvent = null;
  try {
    ({ CartLinesUpdateEvent } = await import('@shopify/events'));
  } catch (error) {
    CartLinesUpdateEvent = null;
  }

  // Only without the events module: Shopify's cart action. It resolves without the
  // rendered cart, so the drawer would open empty for a moment — hence the AJAX path first.
  const actions = window.Shopify && window.Shopify.actions;
  if (!CartLinesUpdateEvent && actions && typeof actions.updateCart === 'function') {
    const result = await actions.updateCart(
      { lines: [{ merchandiseId: String(variantId), quantity }] },
      { event: { context: 'product', detail: { source: 'fl-quick-buy' } } }
    );
    if (result && result.userErrors && result.userErrors.length) {
      throw new Error(result.userErrors[0].message || 'Could not add to cart');
    }
    return result;
  }

  try {
    if (!CartLinesUpdateEvent) throw new Error('no events module');
    deferred = CartLinesUpdateEvent.createPromise();
    source.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity }],
        promise: deferred.promise,
      })
    );
  } catch (error) {
    deferred = null;
  }

  // Ask for the rendered cart sections in the same request (like Horizon's own
  // product form), so the drawer is morphed before it opens — no empty-cart flash.
  const sectionIds = [
    ...new Set(
      Array.from(document.querySelectorAll('cart-items-component[data-section-id]'), (el) => el.dataset.sectionId)
    ),
  ].filter(Boolean);
  const payload = { items: [{ id: Number(variantId), quantity }] };
  if (sectionIds.length) {
    payload.sections = sectionIds.join(',');
    payload.sections_url = window.location.pathname;
  }

  const addUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
  const response = await fetch(addUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await response.json();

  if (!response.ok || data.status) {
    const message = data.description || data.message || 'Could not add to cart';
    if (deferred) deferred.reject(new Error(message));
    throw new Error(message);
  }

  if (deferred && CartLinesUpdateEvent) {
    const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'fl-quick-buy',
        itemCount: quantity,
        didError: false,
        sections: data.sections || undefined,
      },
    });
  } else {
    window.location.href = '/cart';
  }

  return data;
}

/* ------------------------------------------------------------------ */
/* <fl-video>                                                          */
/* ------------------------------------------------------------------ */

class FlVideo extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    this.video = this.querySelector('video');
    try {
      const raw = this.querySelector('script[data-fl-sources]');
      this.sources = raw ? JSON.parse(raw.textContent || '{}') : {};
    } catch (error) {
      this.sources = {};
    }

    const hasSources =
      (this.sources.desktop && this.sources.desktop.length) || (this.sources.mobile && this.sources.mobile.length);
    if (!this.video || !hasSources || saveData) return;

    this.phone = window.matchMedia('(max-width: 749px)');
    this.visible = false;

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.visible = entry.isIntersecting;
          if (entry.isIntersecting) this.start();
          else this.video.pause();
        }
      },
      { rootMargin: '240px 0px' }
    );
    this.observer.observe(this);

    this.onVisibility = () => {
      if (document.hidden) this.video.pause();
      else if (this.visible) this.start();
    };
    document.addEventListener('visibilitychange', this.onVisibility);

    this.onMotion = () => {
      if (reduceMotion.matches) {
        this.video.pause();
        this.classList.remove('is-playing');
      } else if (this.visible) {
        this.start();
      }
    };
    reduceMotion.addEventListener('change', this.onMotion);

    this.onViewport = () => {
      this.loadedFor = null;
      if (this.visible) this.start();
    };
    this.phone.addEventListener('change', this.onViewport);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.onMotion) reduceMotion.removeEventListener('change', this.onMotion);
    if (this.onViewport && this.phone) this.phone.removeEventListener('change', this.onViewport);
  }

  /** Picks HLS on Safari, otherwise the smallest MP4 that still looks sharp. */
  pickSource() {
    const usePhone = this.phone.matches && this.sources.mobile && this.sources.mobile.length;
    const list = (usePhone ? this.sources.mobile : this.sources.desktop) || this.sources.mobile || [];
    if (!list.length) return null;

    const hls = list.find((s) => /mpegurl/i.test(s.type));
    if (hls && this.video.canPlayType('application/vnd.apple.mpegurl')) return hls;

    const mp4 = list.filter((s) => /mp4/i.test(s.type));
    if (!mp4.length) return list[0];

    const sized = mp4.filter((s) => s.w || s.h).sort((a, b) => Math.max(a.w, a.h) - Math.max(b.w, b.h));
    if (!sized.length) return mp4[0];

    const box = this.getBoundingClientRect();
    const need = Math.max(box.width, box.height) * Math.min(window.devicePixelRatio || 1, 2) * 0.75;
    return sized.find((s) => Math.max(s.w, s.h) >= need) || sized[sized.length - 1];
  }

  start() {
    if (reduceMotion.matches) return;
    const key = this.phone.matches ? 'phone' : 'wide';

    if (this.loadedFor !== key) {
      const source = this.pickSource();
      if (!source) return;
      this.loadedFor = key;
      this.classList.remove('is-playing');
      this.video.addEventListener('playing', () => this.classList.add('is-playing'), { once: true });
      this.video.src = source.src;
      this.video.load();
    }

    const attempt = this.video.play();
    if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
  }
}

/* ------------------------------------------------------------------ */
/* <fl-countdown data-target="2026-10-16T18:00:00+02:00">              */
/* ------------------------------------------------------------------ */

class FlCountdown extends HTMLElement {
  connectedCallback() {
    this.target = Date.parse(this.dataset.target || '');
    if (Number.isNaN(this.target)) return;

    this.slots = {
      d: this.querySelector('[data-unit="d"]'),
      h: this.querySelector('[data-unit="h"]'),
      m: this.querySelector('[data-unit="m"]'),
      s: this.querySelector('[data-unit="s"]'),
    };

    this.tick();
    this.timer = window.setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
  }

  tick() {
    let left = this.target - Date.now();

    if (left <= 0) {
      window.clearInterval(this.timer);
      this.toggleAttribute('data-done', true);
      const scope = this.closest('[data-fl-launch]');
      if (scope) scope.classList.add('is-live');
      left = 0;
    }

    const day = 86400000;
    const days = Math.floor(left / day);
    const hours = Math.floor((left % day) / 3600000);
    const minutes = Math.floor((left % 3600000) / 60000);
    const seconds = Math.floor((left % 60000) / 1000);
    const pad = (n) => String(n).padStart(2, '0');

    if (this.slots.d) this.slots.d.textContent = pad(days);
    if (this.slots.h) this.slots.h.textContent = pad(hours);
    if (this.slots.m) this.slots.m.textContent = pad(minutes);
    if (this.slots.s) this.slots.s.textContent = pad(seconds);
  }
}

/* ------------------------------------------------------------------ */
/* <fl-flip> – tap a shirt to see the back                              */
/* ------------------------------------------------------------------ */

class FlFlip extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button || this.dataset.ready) return;
    this.dataset.ready = 'true';
    // The visible label changes ("See back" / "See front") and is the accessible
    // name, so the button is not a toggle (no aria-pressed).
    this.button.addEventListener('click', () => {
      this.toggleAttribute('data-back', !this.hasAttribute('data-back'));
    });
  }
}

/* ------------------------------------------------------------------ */
/* <fl-quick-buy> – pick a size, add to cart                           */
/* ------------------------------------------------------------------ */

class FlQuickBuy extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    this.form = this.querySelector('form');
    this.button = this.querySelector('[data-fl-submit]');
    this.label = this.querySelector('[data-fl-label]');
    this.status = this.querySelector('[data-fl-status]');
    if (!this.form || !this.button || !this.label) return;

    this.form.addEventListener('change', () => this.sync());
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.submit();
    });
    this.sync();
  }

  get selected() {
    return this.form.querySelector('input[name="id"]:checked');
  }

  sync() {
    const choice = this.selected;
    this.button.disabled = !choice;
    this.label.textContent = choice ? this.dataset.textAdd : this.dataset.textChoose;
    if (this.status) this.status.textContent = '';
  }

  async submit() {
    const choice = this.selected;
    if (!choice || this.busy) return;

    this.busy = true;
    this.button.disabled = true;
    this.setAttribute('aria-busy', 'true');
    this.label.textContent = this.dataset.textAdding || 'Adding…';

    try {
      await addToCart(choice.value, 1, this);
      this.label.textContent = this.dataset.textAdded;
      if (this.status) this.status.textContent = this.dataset.textAdded;
      window.setTimeout(() => this.sync(), 2400);
    } catch (error) {
      if (this.status) this.status.textContent = error.message;
      this.label.textContent = this.dataset.textAdd;
    } finally {
      this.busy = false;
      this.removeAttribute('aria-busy');
      this.button.disabled = !this.selected;
    }
  }
}

/* ------------------------------------------------------------------ */
/* Delegated helpers (survive Horizon's cart-drawer morphing)          */
/* ------------------------------------------------------------------ */

document.addEventListener('click', async (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const addButton = target.closest('[data-fl-add-variant]');
  if (addButton instanceof HTMLButtonElement) {
    event.preventDefault();
    if (addButton.getAttribute('aria-busy') === 'true') return;
    addButton.setAttribute('aria-busy', 'true');
    addButton.disabled = true;
    try {
      await addToCart(addButton.dataset.flAddVariant, 1, addButton);
    } catch (error) {
      addButton.title = error.message;
    } finally {
      addButton.removeAttribute('aria-busy');
      addButton.disabled = false;
    }
    return;
  }

  const unitButton = target.closest('[data-fl-unit]');
  if (unitButton) {
    const scope = unitButton.closest('dialog') || document;
    const unit = unitButton.getAttribute('data-fl-unit');
    scope.querySelectorAll('[data-fl-unit]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button === unitButton));
    });
    scope.querySelectorAll('td[data-cm]').forEach((cell) => {
      const cm = parseFloat(cell.getAttribute('data-cm') || '');
      if (Number.isNaN(cm)) return;
      cell.textContent = unit === 'in' ? (cm / 2.54).toFixed(1) : cell.getAttribute('data-cm');
    });
    return;
  }

  const inlineGuide = target.closest('[data-fl-sg-inline]');
  if (inlineGuide) {
    const scope = inlineGuide.closest('.product-details') || inlineGuide.closest('.shopify-section') || document;
    const guideOpener = scope.querySelector('.fl-sg__open[data-fl-dialog-open]');
    if (guideOpener instanceof HTMLElement) {
      event.preventDefault();
      guideOpener.click();
    }
    return;
  }

  const opener = target.closest('[data-fl-dialog-open]');
  if (opener) {
    const dialog = document.getElementById(opener.getAttribute('data-fl-dialog-open'));
    if (dialog instanceof HTMLDialogElement) {
      event.preventDefault();
      // Size guide: mark the size the shopper already picked
      const scope = opener.closest('.shopify-section') || document;
      const picked = scope.querySelector('variant-picker input:checked');
      const size = picked instanceof HTMLInputElement ? picked.value.trim().toUpperCase() : '';
      dialog.querySelectorAll('tr[data-size]').forEach((row) => {
        row.toggleAttribute('data-current', Boolean(size) && row.getAttribute('data-size') === size);
      });
      dialog.showModal();
    }
    return;
  }

  const closer = target.closest('[data-fl-dialog-close]');
  if (closer) {
    const dialog = closer.closest('dialog');
    if (dialog) dialog.close();
    return;
  }

  if (target instanceof HTMLDialogElement && target.classList.contains('fl-dialog')) {
    // Click on the backdrop area closes the dialog
    const rect = target.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
    if (!inside) target.close();
  }
});

/* ------------------------------------------------------------------ */

const define = (name, element) => {
  if (!customElements.get(name)) customElements.define(name, element);
};

define('fl-video', FlVideo);
define('fl-countdown', FlCountdown);
define('fl-flip', FlFlip);
define('fl-quick-buy', FlQuickBuy);

/* ------------------------------------------------------------------ */
/* Product page: apparel sizes are not pre-selected                     */
/* (variant picker renders without a checked size; see                  */
/*  snippets/variant-main-picker.liquid → data-fl-require-size)         */
/* ------------------------------------------------------------------ */

/** Where a product form lives: the quick-add dialog or the product section. */
function sizeScope(element) {
  return element.closest('.quick-add-modal, .shopify-section') || document.body;
}

/** @param {Element} picker */
function sizeFieldsets(picker) {
  return Array.from(picker.querySelectorAll('fieldset'));
}

/** True while one of the picker's options has no checked value (tee: the size; bundle: any tee's size). */
function sizesIncomplete(picker) {
  const sets = sizeFieldsets(picker);
  if (!sets.length) return !picker.querySelector('input:checked');
  return sets.some((fieldset) => !fieldset.querySelector('input:checked'));
}

/** The scope's size picker, if it still waits for a size. */
function pendingSizePicker(scope) {
  const picker = scope.querySelector('variant-picker[data-fl-require-size]');
  return picker && sizesIncomplete(picker) ? picker : null;
}

function pointToSizes(picker) {
  const open = sizeFieldsets(picker).find((fieldset) => !fieldset.querySelector('input:checked'));
  const target = open || picker;
  target.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'center' });
  picker.classList.remove('fl-attention');
  void picker.offsetWidth;
  picker.classList.add('fl-attention');
  const first = target.querySelector('input[type="radio"]:not([aria-disabled="true"]):not(:disabled)');
  if (first instanceof HTMLElement) first.focus({ preventScroll: true });
}

/** Wallet buttons (Shop Pay, PayPal …) can't be stopped from inside their iframes: make them inert until a size is picked. */
function syncWalletLock() {
  document.querySelectorAll('variant-picker[data-fl-require-size]').forEach((picker) => {
    const scope = sizeScope(picker);
    const pending = sizesIncomplete(picker);
    scope.querySelectorAll('.accelerated-checkout-block > *').forEach((wallet) => {
      if (wallet.inert !== pending) wallet.inert = pending;
    });
  });
}

/*
 * Bundles (one size option per tee): Horizon answers the first pick with a full
 * variant, so the other tee would silently get the first size again. Remember
 * which options the shopper picked and keep the others unchecked until they are
 * chosen, too. A bundle opened with ?variant= (cart link, reload) is left alone.
 */
const flTouched = new WeakMap();

function trackMultiSize(root = document) {
  root.querySelectorAll('variant-picker[data-fl-multi-size][data-fl-require-size]').forEach((picker) => {
    if (!flTouched.has(picker)) flTouched.set(picker, new Set());
  });
}

/** @param {Element} picker */
function applyMultiSize(picker) {
  const touched = flTouched.get(picker);
  if (!touched) return;
  const sets = sizeFieldsets(picker);
  sets.forEach((fieldset, index) => {
    if (touched.has(index)) return;
    fieldset.querySelectorAll('input:checked').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.dataset.currentChecked = 'false';
      }
    });
  });
  const incomplete = sets.some((fieldset, index) => !touched.has(index) || !fieldset.querySelector('input:checked'));
  picker.toggleAttribute('data-fl-require-size', incomplete);
  picker.toggleAttribute('data-fl-incomplete', incomplete);
  syncWalletLock();
}

function initMultiSize() {
  if (window.__flMultiSize) return;
  window.__flMultiSize = true;
  trackMultiSize();

  window.addEventListener(
    'change',
    (event) => {
      const input = event.target instanceof HTMLInputElement ? event.target : null;
      const picker = input && input.closest('variant-picker[data-fl-multi-size]');
      const touched = picker && flTouched.get(picker);
      if (!touched) return;
      const index = sizeFieldsets(picker).indexOf(input.closest('fieldset'));
      if (index > -1) touched.add(index);
    },
    true
  );

  // Horizon re-renders the picker before it resolves the select event's promise.
  document.addEventListener('shopify:product:select', async (event) => {
    const picker = event.target instanceof Element ? event.target.closest('variant-picker[data-fl-multi-size]') : null;
    if (!picker || !flTouched.has(picker)) return;
    try {
      await /** @type {any} */ (event).promise;
    } catch (error) {
      return;
    }
    applyMultiSize(picker);
  });

  // Quick-add dialogs render their picker later.
  new MutationObserver(() => trackMultiSize()).observe(document.body, { childList: true, subtree: true });
}

/*
 * While no size is chosen, every way to buy (main button, sticky bar, quick-add
 * dialog, Enter in the quantity field, wallet buttons) points to the size picker
 * instead of adding the first size behind the shopper's back. The listeners sit on
 * window in the capture phase so they run before Horizon's delegated handlers
 * (document, capture) — no "added" animation, no fly-to-cart, no request.
 * The "Select a size" label and the greyed wallet buttons are pure CSS (fl-base.css).
 */
/**
 * A size picked before Horizon's scripts were ready never reached the hidden
 * variant id (it still holds the first size). Copy the checked size's variant
 * id into the form right before an add goes through.
 * @param {Element} scope
 */
function syncVariantId(scope) {
  const picker = scope.querySelector('variant-picker[data-fl-require-size]');
  // Only for one size option: a bundle's inputs carry combination ids, Horizon resolves those itself.
  if (picker && sizeFieldsets(picker).length > 1) return;
  const checked = picker && picker.querySelector('input:checked');
  const id = checked instanceof HTMLInputElement ? checked.dataset.variantId : '';
  if (!id) return;
  scope.querySelectorAll('product-form-component input[name="id"]').forEach((input) => {
    if (input instanceof HTMLInputElement && input.value !== id) input.value = id;
  });
}

function initRequireSize() {
  if (window.__flSizeGuard) return;
  window.__flSizeGuard = true;

  window.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const trigger = target && target.closest('.add-to-cart-button, .accelerated-checkout-block');
      if (!trigger) return;
      const scope = sizeScope(trigger);
      const picker = pendingSizePicker(scope);
      if (!picker) {
        syncVariantId(scope);
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      pointToSizes(picker);
    },
    true
  );

  window.addEventListener(
    'submit',
    (event) => {
      const form = event.target instanceof HTMLFormElement ? event.target : null;
      if (!form || !form.closest('product-form-component')) return;
      const scope = sizeScope(form);
      const picker = pendingSizePicker(scope);
      if (!picker) {
        syncVariantId(scope);
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      pointToSizes(picker);
    },
    true
  );

  // Keep wallet buttons in step with the picker: on size changes and whenever
  // Horizon inserts or morphs product markup (variant switch, quick-add dialog).
  let queued = false;
  const queueSync = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      syncWalletLock();
    });
  };
  window.addEventListener('change', queueSync, true);
  new MutationObserver(queueSync).observe(document.body, { childList: true, subtree: true });
  syncWalletLock();
}

/* ------------------------------------------------------------------ */
/* Back-side images: load + decode before the first turn, so the flip   */
/* never stalls on image decoding (hero: mouse devices only).           */
/* ------------------------------------------------------------------ */

function warmBackImages() {
  if (!('IntersectionObserver' in window)) return;
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        entry.target
          .querySelectorAll('img.fl-hero-shot__back, img.fl-drop-card__back, img.fl-pw-tee__back')
          .forEach((img) => {
            img.loading = 'eager';
            if (typeof img.decode === 'function') img.decode().catch(() => {});
          });
      });
    },
    { rootMargin: '300px 0px' }
  );
  const targets = ['.fl-drop-card__flip', '.fl-pw-tee'];
  if (fine) targets.push('.fl-hero__shots');
  document.querySelectorAll(targets.join(',')).forEach((element) => observer.observe(element));
}

/* ------------------------------------------------------------------ */
/* Cart drawer: when it opens by itself                                */
/* Theme settings → Cart: "Wann automatisch öffnen" (data-fl-open)      */
/*   first   – only for the first item in an empty cart (default)      */
/*   always  – after every add (Horizon's behaviour)                    */
/*   desktop – only where the cart opens at the side (990px and up)     */
/* Horizon's own checkbox switches auto-open off completely.            */
/* ------------------------------------------------------------------ */

// Below 990px Horizon lays the cart over the whole page
const cartOverPage = window.matchMedia('(max-width: 989px)');

/** Short pulse on the header cart icon when an item went in and the cart stayed closed */
function pulseCartIcon() {
  document.querySelectorAll('cart-icon').forEach((icon) => {
    icon.classList.remove('fl-cart-pulse');
    void icon.offsetWidth; // restart the animation
    icon.classList.add('fl-cart-pulse');
    window.setTimeout(() => icon.classList.remove('fl-cart-pulse'), 1200);
  });
}

async function initCartOpen() {
  let StandardEvents = null;
  try {
    ({ StandardEvents } = await import('@shopify/events'));
  } catch (error) {
    return;
  }
  if (!StandardEvents || !StandardEvents.cartLinesUpdate) return;

  // Capture phase: runs before Horizon's cart drawer decides whether to open
  document.addEventListener(
    StandardEvents.cartLinesUpdate,
    (event) => {
      if (event.action !== 'add') return;
      const drawer = document.querySelector('cart-drawer-component[auto-open]');
      if (!drawer) return; // auto-open switched off in the theme settings
      const dialog = drawer.closest('dialog');
      if (dialog && dialog.open) return; // added from inside the open cart (upsell)

      const mode = drawer.dataset.flOpen || 'always';
      const cartWasEmpty = Boolean(drawer.querySelector('.cart-drawer--empty'));
      const stayClosed = (mode === 'first' && !cartWasEmpty) || (mode === 'desktop' && cartOverPage.matches);
      if (!stayClosed) return;

      // Horizon reads the attribute right now, in its own listener for this event
      drawer.removeAttribute('auto-open');
      window.setTimeout(() => drawer.setAttribute('auto-open', ''), 0);

      const promise = event.promise;
      if (promise && typeof promise.then === 'function') {
        promise
          .then((result) => {
            if (!(result && result.detail && result.detail.didError)) pulseCartIcon();
          })
          .catch(() => {});
      } else {
        pulseCartIcon();
      }
    },
    true
  );
}

const onReady = (fn) => {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true });
  else fn();
};

onReady(() => {
  initRequireSize();
  initMultiSize();
  warmBackImages();
  initCartOpen();
});
